// Staff-managed offers on the Concierge Schools tab, via the athlete's own
// /athletes/offers routes with ?as_athlete_id. Two traps in that upsert
// (spec Amendment 2): a missing `sport` is rewritten to "Football", and any
// `committed` key toggles the athlete's commitment — so we always send sport and
// never send committed.
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../../context/AuthContext';
import CollegeLogo from '../../CollegeLogo';
import CollegePicker from '../../CollegePicker';
import { fmtMonDay } from '../../../lib/conciergeActivityModel';

const TYPES = [
  { v: 'full', l: 'Full scholarship', tone: 'green' },
  { v: 'partial', l: 'Partial scholarship', tone: 'green' },
  { v: 'pwo', l: 'Preferred walk-on', tone: 'neutral' },
  { v: 'verbal', l: 'Verbal', tone: 'neutral' },
];
const typeOf = (v) => TYPES.find(t => t.v === v) || TYPES[0];
const MAX_SLOTS = 10;
const cfgFor = (userId) => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  params: { as_athlete_id: userId },
});

export default function OffersCard({ userId, onSaved, athleteSport }) {
  const { API_URL } = useAuth();
  const [offers, setOffers] = useState(null);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);   // draft object or null
  const [query, setQuery] = useState('');
  const [saving, setSaving] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const deletingRef = useRef(false);

  useEffect(() => {
    // A failed load leaves `offers` null: an empty list here would let "+ Add offer"
    // pick slot 1 and the server's upsert-by-slot overwrite a real offer.
    setOffers(null);
    setLoadFailed(false);
    axios.get(`${API_URL}/athletes/offers`, cfgFor(userId))
      .then(res => setOffers(Array.isArray(res.data) ? res.data : []))
      .catch(err => { setLoadFailed(true); setError(err.response?.data?.detail || 'Failed to load offers.'); });
  }, [userId, API_URL]);

  const startAdd = () => {
    if (!Array.isArray(offers)) return;   // only after a successful load
    const used = new Set(offers.map(o => o.slot_number));
    let slot = 1;
    while (used.has(slot) && slot <= MAX_SLOTS) slot += 1;
    if (slot > MAX_SLOTS) { setError('An athlete can have at most 10 offers.'); return; }
    setQuery('');
    setEditing({ slot_number: slot, unitid: null, college_name: '', college_city: '', college_state: '',
      sport: athleteSport || 'Football', offer_type: 'full', offer_date: '', amount: '', notes: '' });
  };

  const startEdit = (o) => { setQuery(o.college_name); setEditing({ ...o }); };

  const save = () => {
    if (!editing?.college_name) return;
    const { slot_number, unitid, college_name, college_city, college_state, sport: rowSport, offer_type, offer_date, amount, notes } = editing;
    const sport = rowSport || athleteSport || 'Football';
    setSaving(true);
    setError('');
    axios.post(`${API_URL}/athletes/offers`,
      { slot_number, unitid, college_name, college_city, college_state, sport, offer_type,
        offer_date: offer_date || null, amount: amount || '', notes: notes || '' }, cfgFor(userId))
      .then(res => {
        setOffers(prev => {
          const rest = (prev || []).filter(o => o.slot_number !== res.data.slot_number);
          return [...rest, res.data].sort((a, b) => a.slot_number - b.slot_number);
        });
        setEditing(null);
        if (onSaved) onSaved();
      })
      .catch(err => setError(err.response?.data?.detail || 'Failed to save the offer.'))
      .finally(() => setSaving(false));
  };

  const remove = (o) => {
    if (deletingRef.current) return;
    deletingRef.current = true;
    setDeleting(true);
    axios.delete(`${API_URL}/athletes/offers/${o.id}`, cfgFor(userId))
      .then(() => { setOffers(prev => (prev || []).filter(x => x.id !== o.id)); setEditing(null); if (onSaved) onSaved(); })
      .catch(err => setError(err.response?.data?.detail || 'Failed to delete the offer.'))
      .finally(() => { deletingRef.current = false; setDeleting(false); });
  };

  const set = (patch) => setEditing(e => ({ ...e, ...patch }));

  return (
    <section className="cn-card">
      <div className="cn-card__head">
        <h2 className="cn-card__title">Offers</h2>
        {offers && offers.length > 0 && <span className="cn-pill cn-pill--solid">{offers.length}</span>}
        <span className="cn-card__sub">Visible on the athlete's profile</span>
        {!editing && Array.isArray(offers) && <button type="button" className="cn-dbtn" onClick={startAdd}>+ Add offer</button>}
      </div>
      {error && <div className="cn-card__err">{error}</div>}
      {editing && (
        <div className="cn-offer-form">
          {editing.id ? <div className="cn-tsrow__name">{editing.college_name}</div> : (
            <CollegePicker value={query} onInputChange={setQuery} placeholder="Search college…"
              ariaLabel="College for this offer" className="cn-vinput" rootStyle={{ flex: 1, minWidth: 0 }}
              onSelect={c => { setQuery(c.n); set({ unitid: c.u ?? null, college_name: c.n, college_city: c.c || '', college_state: c.s || '' }); }}
              classNames={{ popup: 'cn-autocomplete', option: 'cn-autocomplete__item',
                optionActive: 'cn-autocomplete__item--active', footer: 'cn-autocomplete__hint' }}
              renderItem={c => (<><div className="cn-autocomplete__name">{c.n}</div>
                <div className="cn-autocomplete__loc">{[c.c, c.s].filter(Boolean).join(', ')}</div></>)} />
          )}
          <select className="cn-vinput" aria-label="Offer type" value={editing.offer_type} onChange={e => set({ offer_type: e.target.value })}>
            {TYPES.map(t => <option key={t.v} value={t.v}>{t.l}</option>)}
          </select>
          <input type="date" className="cn-vinput" aria-label="Offered date" value={editing.offer_date || ''}
            onChange={e => set({ offer_date: e.target.value })} />
          <input className="cn-vinput" aria-label="Amount" placeholder="Amount" value={editing.amount || ''}
            onChange={e => set({ amount: e.target.value })} />
          <input className="cn-vinput" aria-label="Notes" placeholder="Notes" value={editing.notes || ''}
            onChange={e => set({ notes: e.target.value })} />
          <div className="cn-offer-form__actions">
            {editing.id && <button type="button" className="cn-remove" onClick={() => remove(editing)} disabled={deleting}>Delete</button>}
            <button type="button" className="cn-hbtn" onClick={() => setEditing(null)}>Cancel</button>
            <button type="button" className="cn-pbtn" onClick={save} disabled={saving || !editing.college_name}>Save offer</button>
          </div>
        </div>
      )}
      {loadFailed ? null : offers === null ? <div className="cn-loading">Loading…</div>
        : offers.length === 0 ? <div className="cn-empty-line">No offers yet.</div>
          : (
            <>
              <div className="cn-offrow cn-tsrow--head"><div>School</div><div>Type</div><div>Offered</div><div>Amount &amp; notes</div><div /></div>
              {offers.map(o => (
                <div className="cn-offrow" key={o.id}>
                  <div className="cn-tsrow__school">
                    <CollegeLogo schoolName={o.college_name} logoUrl={o.logo_url} unitid={o.unitid} size={32} />
                    <div><div className="cn-tsrow__name">{o.college_name}</div>
                      <div className="cn-tsrow__loc">{[o.college_city, o.college_state].filter(Boolean).join(', ')}</div></div>
                  </div>
                  <div><span className={`cn-pill cn-pill--${typeOf(o.offer_type).tone}`}>{typeOf(o.offer_type).l}</span></div>
                  <div>{o.offer_date ? fmtMonDay(o.offer_date) : '—'}</div>
                  <div className="cn-offrow__notes">{[o.amount, o.notes].filter(Boolean).join(' · ') || '—'}</div>
                  <button type="button" className="cn-card__action" aria-label={`Edit ${o.college_name}`} onClick={() => startEdit(o)}>Edit</button>
                </div>
              ))}
            </>
          )}
    </section>
  );
}
